/**
 * cart-live.js — update the cart in place instead of reloading the page.
 *
 * The custom cart page writes line-item properties (trade-in, protection plan,
 * installation) through the cart AJAX API. Every one of those edits used to end
 * in `window.location.reload()`, which throws away scroll position, focus and
 * ~a second of the shopper's time for what is really a one-row change.
 *
 * This module replaces that with a render pass:
 *
 *   1. mutate the cart through `cartLive.post()`, which asks the Section
 *      Rendering API for fresh HTML in the same request (no extra round trip);
 *   2. `cartLive.render()` morphs that HTML into the live DOM.
 *
 * The morph updates matching nodes in place rather than replacing subtrees, so
 * unchanged markup is never touched, <select>s keep their DOM identity, and
 * focus, scroll position and whatever the shopper is currently typing survive.
 *
 * Contract for the scripts that drive it:
 *   - wrap a mutation in `cartLive.mutate(el, fn)` for the single-flight lock
 *     plus the pending/updated styling on `el`'s row;
 *   - listen for `cart:live-render` on document to re-apply anything that can't
 *     come from the server HTML (the plan picker's price-dependent <option>s);
 *   - add `data-cart-morph-preserve` to markup the morph must leave alone;
 *   - add `data-morph-key` to list items so they're matched by identity rather
 *     than by position.
 *
 * Loaded by sections/custom-cart.liquid and snippets/custom-cart-drawer.liquid.
 */
(function () {
  'use strict';

  // The cart page installs a reload-based shim for the window before this
  // deferred module arrives; anything else means we're already loaded.
  if (window.cartLive && !window.cartLive.isShim) return;

  const PRESERVE = 'data-cart-morph-preserve';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ============================================================
     Morph — make `target` look like `source` without rebuilding it
     ============================================================ */

  /**
   * Keys are opt-in. `id` deliberately isn't a fallback: this theme's cart ids
   * encode line position (`Quantity-3`, `CartItem-3`), so removing a line
   * renumbers every line after it. Treating those as identity made the morph
   * delete and re-insert matching nodes instead of updating them — and
   * QuantityInput caches `this.input` in its constructor, so it was left
   * holding a detached input and the stepper silently stopped working.
   */
  function keyFor(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    return node.getAttribute('data-morph-key');
  }

  function syncAttributes(target, source) {
    for (const attr of Array.from(target.attributes)) {
      if (!source.hasAttribute(attr.name)) target.removeAttribute(attr.name);
    }
    for (const attr of Array.from(source.attributes)) {
      if (target.getAttribute(attr.name) !== attr.value) target.setAttribute(attr.name, attr.value);
    }
  }

  /* The shopper's in-progress input always beats the server copy — both what
     they're focused on, and any control on a row whose own edit hasn't landed
     yet (another row's render must not snap it back). */
  function syncFormValue(target, source) {
    if (target === document.activeElement) return;
    if (target.closest && target.closest('.is-cart-pending')) return;

    if (target.tagName === 'INPUT') {
      if (target.type === 'checkbox' || target.type === 'radio') {
        target.checked = source.hasAttribute('checked');
      } else if (target.type !== 'file') {
        target.value = source.getAttribute('value') || '';
      }
      return;
    }

    if (target.tagName === 'TEXTAREA') {
      // Only when untouched — don't wipe a half-written cart note.
      if (target.value === target.defaultValue) target.value = source.textContent;
      target.defaultValue = source.textContent;
      return;
    }

    if (target.tagName === 'OPTION') {
      target.selected = source.hasAttribute('selected');
    }
  }

  function morphNode(target, source) {
    if (target.nodeType !== source.nodeType || target.nodeName !== source.nodeName) {
      target.replaceWith(source.cloneNode(true));
      return;
    }

    if (target.nodeType === Node.TEXT_NODE || target.nodeType === Node.COMMENT_NODE) {
      if (target.nodeValue !== source.nodeValue) target.nodeValue = source.nodeValue;
      return;
    }

    if (target.nodeType !== Node.ELEMENT_NODE) return;
    if (target.hasAttribute(PRESERVE)) return;

    syncAttributes(target, source);

    if (target.tagName === 'SELECT') {
      // An open/focused select keeps the value the shopper just picked, even
      // though its <option> list is rebuilt from the server markup.
      const keep = target === document.activeElement ? target.value : null;
      morphChildren(target, source);
      if (keep !== null && target.querySelector('option[value="' + CSS.escape(keep) + '"]')) {
        target.value = keep;
      }
      return;
    }

    morphChildren(target, source);
    syncFormValue(target, source);
  }

  function morphChildren(target, source) {
    let targetChild = target.firstChild;
    let sourceChild = source.firstChild;

    while (sourceChild) {
      const nextSource = sourceChild.nextSibling;

      if (!targetChild) {
        target.appendChild(sourceChild.cloneNode(true));
        sourceChild = nextSource;
        continue;
      }

      const sourceKey = keyFor(sourceChild);

      if (sourceKey) {
        // Keyed: find this node anywhere ahead of the cursor and move it here,
        // so reordering a list doesn't rebuild every row after the change.
        let match = targetChild;
        while (match && keyFor(match) !== sourceKey) match = match.nextSibling;

        if (match) {
          if (match !== targetChild) target.insertBefore(match, targetChild);
          const nextTarget = match.nextSibling;
          morphNode(match, sourceChild);
          targetChild = nextTarget;
        } else {
          target.insertBefore(sourceChild.cloneNode(true), targetChild);
        }

        sourceChild = nextSource;
        continue;
      }

      const nextTarget = targetChild.nextSibling;
      morphNode(targetChild, sourceChild);
      targetChild = nextTarget;
      sourceChild = nextSource;
    }

    while (targetChild) {
      const nextTarget = targetChild.nextSibling;
      target.removeChild(targetChild);
      targetChild = nextTarget;
    }
  }

  /* ============================================================
     Which sections we re-render, and where their HTML lands
     ============================================================ */

  function paneList() {
    const panes = [];

    const mainCart = document.getElementById('main-cart');
    if (mainCart && mainCart.dataset.id) {
      ['.js-cart-items', '.js-subtotal', '.js-free-shipping-bar'].forEach((selector) => {
        if (document.querySelector(selector)) panes.push({ section: mainCart.dataset.id, selector: selector });
      });
    }

    // The drawer is a snippet rendered through sections/cart-drawer.liquid, so
    // the section renderer returns exactly the markup that is on the page.
    if (document.querySelector('#CartDrawer .drawer__inner')) {
      panes.push({ section: 'cart-drawer', selector: '#CartDrawer .drawer__inner' });
    }

    if (document.getElementById('cart-icon-bubble')) {
      panes.push({ section: 'cart-icon-bubble', selector: '.shopify-section', target: '#cart-icon-bubble' });
    }

    return panes;
  }

  function sectionIds(panes) {
    return Array.from(new Set(panes.map((pane) => pane.section)));
  }

  function sectionsPayload() {
    const ids = sectionIds(paneList());
    if (!ids.length) return {};
    return { sections: ids.join(','), sections_url: window.location.pathname };
  }

  async function fetchSections(ids) {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('sections', ids.join(','));
    const res = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!res.ok) throw new Error('section render failed: ' + res.status);
    return res.json();
  }

  /* ============================================================
     Requests — every mutation also brings back the new HTML
     ============================================================ */

  let stashedSections = null; // sections from the most recent mutation
  let stashedCart = null; // cart JSON from the most recent mutation

  function stash(state) {
    if (!state || typeof state !== 'object') return;
    if (state.sections) stashedSections = state.sections;
    // cart/add.js answers with the lines it added, not the whole cart — only
    // keep responses that are a full cart.
    if (Array.isArray(state.items) && typeof state.item_count === 'number') stashedCart = state;
  }

  async function post(path, body) {
    const res = await fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(Object.assign({}, body, sectionsPayload())),
    });
    const state = await res.json().catch(() => ({}));
    if (res.ok) stash(state);
    return { ok: res.ok, status: res.status, state: state };
  }

  async function get(path) {
    const res = await fetch(root() + path, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    return res.json();
  }

  /* ============================================================
     Render
     ============================================================ */

  function toggleEmptyState() {
    const markers = [];
    if (document.querySelector('.js-cart-items')) {
      markers.push(!document.querySelector('.js-cart-items .cart-card'));
    }
    if (document.getElementById('cart-icon-bubble')) {
      markers.push(!document.querySelector('#cart-icon-bubble .cart-count-bubble'));
    }
    const isEmpty = markers.length ? markers.every(Boolean) : false;

    ['#main-cart', 'cart-items', 'cart-drawer', 'cart-drawer-items'].forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => el.classList.toggle('is-empty', isEmpty));
    });

    return isEmpty;
  }

  /**
   * Bundles are grouped in Liquid through the underscore-prefixed `_bundle`
   * properties. If a shop's section renderer ever hides those from Liquid, the
   * re-rendered list would silently lose its plan/addon grouping — so compare
   * the rendered card count against the cart and fall back to a reload once.
   */
  const FALLBACK_FLAG = 'cartLive:sectionPropsUnsupported';

  function verifyBundles(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const list = document.querySelector('.js-cart-items');
    if (!list) return;

    const expected = cart.items.filter((item) => {
      const role = item.properties && item.properties._bundle_role;
      return role !== 'plan' && role !== 'install_addon';
    }).length;
    const rendered = list.querySelectorAll('.cart-card').length;

    if (expected === rendered) {
      try {
        sessionStorage.removeItem(FALLBACK_FLAG);
      } catch (err) {
        /* private mode — nothing to clean up */
      }
      return;
    }

    console.warn('[cart-live] rendered %d cards for %d cart lines — falling back to reload', rendered, expected);

    let alreadyTried = false;
    try {
      alreadyTried = sessionStorage.getItem(FALLBACK_FLAG) === '1';
      sessionStorage.setItem(FALLBACK_FLAG, '1');
    } catch (err) {
      /* ignore */
    }
    if (!alreadyTried) window.location.reload();
  }

  async function render(sections) {
    const panes = paneList();
    if (!panes.length) return;

    const ids = sectionIds(panes);
    let html = sections || stashedSections;
    const cart = stashedCart;
    stashedSections = null;
    stashedCart = null;

    if (!html || ids.some((id) => typeof html[id] !== 'string')) {
      html = Object.assign({}, html, await fetchSections(ids));
    }

    const docs = {};
    panes.forEach((pane) => {
      const raw = html[pane.section];
      if (typeof raw !== 'string') return;

      if (!docs[pane.section]) {
        docs[pane.section] = new DOMParser().parseFromString(
          raw.replace(/<template\b[^>]*>|<\/template>/g, ''),
          'text/html'
        );
      }

      const source = docs[pane.section].querySelector(pane.selector);
      const target = document.querySelector(pane.target || pane.selector);
      if (source && target) morphChildren(target, source);
    });

    const isEmpty = toggleEmptyState();

    if (typeof window.renderSelects === 'function') window.renderSelects();
    if (typeof window.loadDesktopOnlyTemplates === 'function') window.loadDesktopOnlyTemplates();

    document.dispatchEvent(new CustomEvent('cart:live-render', { detail: { empty: isEmpty, cart: cart } }));

    verifyBundles(cart);
  }

  /* ============================================================
     Busy state — one mutation at a time, shown on the affected row
     ============================================================ */

  let inFlight = 0;
  let queue = Promise.resolve();
  const locked = new Set();

  /* Only the row being edited goes inert. Everything else on the page stays
     usable: with two products in the cart, editing one must never deaden the
     other one's pickers. Concurrent edits are serialised by `queue` instead. */
  const LOCK_SELECTOR = [
    '.cart-card__option-select',
    '.cart-install__edit-btn',
    '.install-addon-qty__btn',
    '.install-addon-qty__input',
    '.install-addon-remove',
    '.quantity__input',
    '.quantity__button',
  ].join(', ');

  function rowFor(el) {
    if (!el || typeof el.closest !== 'function') return null;
    return el.closest('.cart-card__addon--install-item, .cart-card, .cart-item, tr');
  }

  function lockControls(row) {
    (row || document).querySelectorAll(LOCK_SELECTOR).forEach((el) => {
      if (el.disabled) return;
      el.disabled = true;
      locked.add(el);
    });
  }

  function unlockControls() {
    locked.forEach((el) => {
      el.disabled = false;
    });
    locked.clear();
  }

  function markPending(row) {
    document.documentElement.setAttribute('data-cart-updating', 'true');
    if (!row) {
      lockControls(null);
      return;
    }
    row.classList.add('is-cart-pending');
    row.setAttribute('aria-busy', 'true');
    lockControls(row);
  }

  function clearPending(row) {
    if (inFlight === 0) document.documentElement.removeAttribute('data-cart-updating');
    unlockControls();
    if (!row) return;
    row.classList.remove('is-cart-pending');
    row.removeAttribute('aria-busy');
    if (!row.isConnected) return;
    // Brief highlight so the shopper can see which row just changed.
    row.classList.add('is-cart-updated');
    setTimeout(() => row.classList.remove('is-cart-updated'), 700);
  }

  function begin(el) {
    inFlight += 1;
    markPending(rowFor(el));
    return true;
  }

  function end() {
    if (inFlight > 0) inFlight -= 1;
    if (inFlight === 0) document.documentElement.removeAttribute('data-cart-updating');
    unlockControls();
  }

  function error(message) {
    document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors').forEach((container) => {
      let node = container.querySelector('.cart-live-error');
      if (!message) {
        if (node) node.remove();
        return;
      }
      if (!node) {
        node = document.createElement('p');
        node.className = 'cart-live-error';
        node.setAttribute('role', 'alert');
        container.appendChild(node);
      }
      node.textContent = message;
    });
    if (message) console.error('[cart-live]', message);
  }

  async function runMutation(row, fn) {
    error(null);
    try {
      const result = await fn();
      // Unlock before the morph so the fresh markup has the last word on which
      // controls are disabled.
      unlockControls();
      if (result === false) return false;
      await render();
      return result === undefined ? true : result;
    } catch (err) {
      console.error('[cart-live] mutation failed', err);
      stashedSections = null;
      stashedCart = null;
      unlockControls();
      try {
        await render();
      } catch (renderErr) {
        console.error('[cart-live] recovery render failed', renderErr);
        window.location.reload();
      }
      return false;
    } finally {
      inFlight -= 1;
      clearPending(row);
    }
  }

  /**
   * Run `fn` as a cart mutation. The row holding `el` goes inert immediately;
   * the rest of the cart stays live. Mutations never overlap — a second one
   * started while the first is in flight waits its turn rather than being
   * dropped — so `fn` must read line keys from the DOM when it runs, not when
   * the event fired.
   */
  function mutate(el, fn) {
    const row = rowFor(el);
    inFlight += 1;
    markPending(row);

    const run = () => runMutation(row, fn);
    const task = queue.then(run, run);
    queue = task.catch(() => {});
    return task;
  }

  window.cartLive = {
    get: get,
    post: post,
    render: render,
    mutate: mutate,
    begin: begin,
    end: end,
    error: error,
    isBusy: function () {
      return inFlight > 0;
    },
    morph: morphChildren,
  };
})();
